import React, { useState } from 'react';
import { EyeIcon, X, ShoppingBag, User, MapPin, CreditCard, CheckCircle, AlertCircle, Hash, Save, Trash2, Loader2 } from 'lucide-react';
import DataTable from 'react-data-table-component';
import { useMutation } from '@apollo/client';
import { UPDATE_ORDER } from '../apollo/Mutations';

const customStyles = {
  header: {
    style: {
      minHeight: '56px',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#0D3B3F',
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
      borderTopColor: 'rgba(255,255,255,0.1)',
      borderRadius: '1.5rem 1.5rem 0 0',
      marginBottom: '8px',
    },
  },
  headCells: {
    style: {
      color: '#EAB308',
      fontSize: '10px',
      fontWeight: '900',
      textTransform: 'uppercase',
      letterSpacing: '0.2em',
      paddingLeft: '24px',
    },
  },
  cells: {
    style: {
      fontSize: '13px',
      color: '#0D3B3F',
      paddingLeft: '24px',
      fontWeight: '600',
    },
  },
  rows: {
    style: {
      minHeight: '72px',
      borderRadius: '1rem',
      backgroundColor: 'white',
      marginBottom: '8px',
      border: '1px solid #F3F4F6',
      '&:hover': {
        backgroundColor: '#F9FAFB',
        transform: 'scale-[1.01]',
        boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    },
  },
  pagination: {
    style: {
      border: 'none',
      fontSize: '11px',
      fontWeight: '800',
      color: '#0D3B3F',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginTop: '12px',
    },
  },
};

const calculateTotal = (items) => {
  return items.reduce((total, item) => {
    const price = parseFloat(item.price || '0');
    return total + price * item.quantity;
  }, 0);
};

export default function OrdersTable({ data, limit }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [loadingUuid, setLoadingUuid] = useState(null);

  const [updateOrder] = useMutation(UPDATE_ORDER);

  const displayedData = limit ? data.slice(0, limit) : data;

  const handleOrderUpdate = async (status) => {
    const uuid = selectedOrder.uuid;
    setLoadingUuid(uuid);

    try {
      const res = await updateOrder({
        variables: { status, uuid },
      });

      const result = res.data.updateOrder;
      setMessage(result.error ? `❌ ${result.message}` : `✅ ${result.message}`);
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setLoadingUuid(null);
      setModalOpen(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const columns = [
    {
      name: 'Order No',
      selector: row => row.orderNo,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-premium-teal/5 flex items-center justify-center text-premium-teal">
            <Hash size={14} />
          </div>
          <span className="font-black tracking-tighter text-premium-teal">{row.orderNo}</span>
        </div>
      )
    },
    {
      name: 'Ordered By',
      selector: row => `${row.orderedBy?.firstName || ''} ${row.orderedBy?.lastName || ''}`,
      sortable: true,
      cell: row => (
        <div className="flex flex-col">
          <span className="font-black text-premium-teal tracking-tight">{row.orderedBy?.firstName} {row.orderedBy?.lastName}</span>
          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em]">{row.orderedBy?.phone}</span>
        </div>
      )
    },
    {
      name: 'Order Status',
      cell: row => {
        let colorClass = '';
        switch (row.status) {
          case 'PENDING': colorClass = 'bg-orange-50 text-orange-600 border-orange-100'; break;
          case 'CANCELLED': colorClass = 'bg-red-50 text-red-600 border-red-100'; break;
          case 'ACCEPTED': colorClass = 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/20'; break;
          case 'PROCESSED': colorClass = 'bg-blue-50 text-blue-600 border-blue-100'; break;
          case 'DELIVERED': colorClass = 'bg-green-50 text-green-600 border-green-100'; break;
          case 'ON_DELIVERY': colorClass = 'bg-premium-teal/10 text-premium-teal border-premium-teal/20'; break;
          default: colorClass = 'bg-gray-50 text-gray-600 border-gray-100';
        }
        return <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${colorClass}`}>{row.status}</span>;
      },
    },
    {
      name: 'Total Cost',
      selector: row => calculateTotal(row.orderItems),
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-2">
          <CreditCard size={14} className="text-premium-gold/50" />
          <span className="font-mono text-sm font-black text-premium-teal">{calculateTotal(row.orderItems).toLocaleString()} Tsh</span>
        </div>
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <button
          onClick={() => {
            setSelectedOrder(row);
            setModalOpen(true);
          }}
          className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-premium-teal hover:bg-premium-teal hover:text-white transition-all transform hover:rotate-6 shadow-sm"
        >
          <EyeIcon size={18} />
        </button>
      ),
      width: '100px'
    },
  ];

  return (
    <div className="relative animate-fade-in-up">
      {message && (
        <div className={`mb-6 p-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-white flex items-center gap-3 shadow-lg ${message.startsWith('✅') ? 'bg-green-500 shadow-green-500/20' : 'bg-red-500 shadow-red-500/20'}`}>
          {message.startsWith('✅') ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message}
        </div>
      )}

      <DataTable
        columns={columns}
        data={displayedData}
        pagination
        customStyles={customStyles}
        noHeader
      />

      {/* Premium Detail Modal */}
      {isModalOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-premium-dark/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-fade-in-up my-auto">

            {/* Modal Header */}
            <div className="bg-gradient-to-br from-premium-teal to-premium-dark p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-premium-gold/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-premium-gold border border-white/10">
                    <ShoppingBag size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white tracking-tighter">Order Details</h3>
                    <p className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em] opacity-80">Order Number: {selectedOrder.orderNo}</p>
                  </div>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-all border border-white/10"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8">
              {/* Order Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-3 mb-3">
                    <User size={16} className="text-premium-gold" />
                    <span className="text-[10px] font-black text-premium-gold uppercase tracking-widest leading-none">Customer Information</span>
                  </div>
                  <p className="text-lg font-black text-premium-teal tracking-tight">{selectedOrder.orderedBy?.firstName} {selectedOrder.orderedBy?.lastName}</p>
                  <p className="text-xs font-mono text-gray-400 mt-1">{selectedOrder.orderedBy?.phone}</p>
                </div>
                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin size={16} className="text-premium-gold" />
                    <span className="text-[10px] font-black text-premium-gold uppercase tracking-widest leading-none">Location Coordinates</span>
                  </div>
                  <p className="text-sm font-bold text-premium-teal">{selectedOrder.latitude} | {selectedOrder.longitude}</p>
                  <p className="text-[9px] font-black text-gray-400 mt-1 uppercase tracking-widest">Localized at Registry</p>
                </div>
              </div>

              {/* Items List */}
              <div>
                <div className="flex items-center justify-between mb-4 px-2">
                  <h4 className="text-[11px] font-black text-premium-teal uppercase tracking-widest flex items-center gap-2">
                    <ShoppingBag size={14} className="text-premium-gold" />
                    Ordered Items
                  </h4>
                  <span className="text-[10px] font-black text-gray-400">{selectedOrder.orderItems.length} Units</span>
                </div>
                <div className="space-y-3">
                  {selectedOrder.orderItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-100 hover:border-premium-gold/30 transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[10px] font-black text-premium-teal">
                          {String(idx + 1).padStart(2, '0')}
                        </div>
                        <div>
                          <p className="font-bold text-premium-teal text-sm tracking-tight">{item.title}</p>
                          <p className="text-[10px] text-gray-400 font-medium">Quantity: {item.quantity} Items</p>
                        </div>
                      </div>
                      <p className="font-mono text-xs font-black text-premium-teal group-hover:text-premium-gold transition-colors">
                        {item.price.toLocaleString()} Tsh
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Summary */}
              <div className="flex items-center justify-between bg-premium-teal/5 p-6 rounded-[1.5rem] border border-premium-teal/10">
                <div className="flex items-center gap-4 font-black">
                  <CreditCard size={20} className="text-premium-gold" />
                  <span className="text-premium-teal text-[11px] uppercase tracking-[0.2em]">Total Cost</span>
                </div>
                <p className="text-2xl font-black text-premium-teal tracking-tighter">
                  {calculateTotal(selectedOrder.orderItems).toLocaleString()} <span className="text-xs text-premium-gold ml-1">Tsh</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col md:flex-row gap-4 pt-4">
                <button
                  onClick={() => handleOrderUpdate('CANCELLED')}
                  disabled={loadingUuid === selectedOrder.uuid}
                  className="flex-1 px-8 py-4 rounded-2xl border-2 border-red-50 text-[11px] font-black text-red-500 uppercase tracking-widest hover:bg-red-50 transition-all flex items-center justify-center gap-3"
                >
                  {loadingUuid === selectedOrder.uuid ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Reject Order
                </button>
                <button
                  onClick={() => handleOrderUpdate('ACCEPTED')}
                  disabled={loadingUuid === selectedOrder.uuid}
                  className="flex-1 premium-button bg-premium-teal text-white px-8 py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest shadow-xl shadow-premium-teal/20 flex items-center justify-center gap-3 transition-all"
                >
                  {loadingUuid === selectedOrder.uuid ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} className="text-premium-gold" />}
                  Accept Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
